import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Bell, LogOut } from 'lucide-react';
import { Logo } from './logo';
import { UserAvatar } from '@/components/users/user-avatar';
import { BottomSheet } from '@/components/ui/bottom-sheet';
import { useAuthStore } from '@/stores/auth-store';
import { useLogout } from '@/hooks/use-auth';

// Eyebrow de seção por rota (font-mono, vermelho primary) — espelha o protótipo
function sectionEyebrow(pathname: string): string {
  if (pathname.startsWith('/dashboard')) return '#01//// painel';
  if (pathname.startsWith('/calendar')) return '#02//// programação';
  if (pathname.startsWith('/marketing')) return '#03//// marketing';
  if (pathname.startsWith('/settings/djs')) return '#04//// djs';
  if (pathname.startsWith('/settings')) return '#04//// conta';
  if (pathname.startsWith('/equipe')) return '#03//// equipe';
  return '';
}

export function Header() {
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();
  const location = useLocation();
  const logout = useLogout();
  const [logoutOpen, setLogoutOpen] = useState(false);

  const eyebrow = sectionEyebrow(location.pathname);

  function handleLogout() {
    setLogoutOpen(false);
    logout.mutate(undefined, { onSuccess: () => navigate('/login') });
  }

  return (
    <>
      <header className="lg:hidden sticky top-0 inset-x-0 z-30 bg-background/88 backdrop-blur-[14px] border-b border-border/70">
        {/* Preenche a área acima do notch/Dynamic Island/barra de endereço */}
        <div className="bg-background/88 pwa-header-spacer" />
        <div className="flex items-center justify-between px-[18px] h-14">
          {/* Esquerda: logo + divisória + eyebrow de seção */}
          <div className="flex min-w-0 items-center gap-4">
            <Logo size="sm" />
            <span className="h-[22px] w-px bg-border" />
            <span className="whitespace-nowrap font-mono text-[10px] font-semibold uppercase tracking-[0.08em] text-primary">
              {eyebrow}
            </span>
          </div>

          {/* Direita: sino + avatar do usuário logado */}
          <div className="flex items-center gap-1">
            <button
              aria-label="Notificações"
              className="relative grid h-9 w-9 place-items-center rounded-full text-foreground active:scale-95 transition"
            >
              <Bell className="h-[18px] w-[18px]" />
              <span className="absolute top-2 right-2 h-1.5 w-1.5 rounded-full bg-primary ring-2 ring-background" />
            </button>
            {user && (
              <Link to="/settings" aria-label="Conta" className="ml-0.5">
                <UserAvatar
                  name={user.name}
                  colorHex={user.color_hex}
                  url={user.avatar_url}
                  size="sm"
                />
              </Link>
            )}
            <button
              onClick={() => setLogoutOpen(true)}
              aria-label="Sair"
              className="grid h-9 w-9 place-items-center rounded-full text-destructive hover:bg-destructive/10 active:scale-95 transition"
            >
              <LogOut className="h-5 w-5" />
            </button>
          </div>
        </div>
      </header>

      <BottomSheet open={logoutOpen} onClose={() => setLogoutOpen(false)} title="Sair da conta?">
        <p className="text-sm text-muted-foreground mb-4">
          Você precisará entrar novamente para acessar o painel.
        </p>
        <div className="flex gap-2">
          <button
            onClick={() => setLogoutOpen(false)}
            className="flex-1 rounded-lg border border-border bg-card px-4 py-3 text-sm font-medium hover:bg-muted"
          >
            Cancelar
          </button>
          <motion.button
            whileTap={{ scale: 0.97 }}
            onClick={handleLogout}
            className="flex-1 rounded-lg bg-destructive px-4 py-3 text-sm font-semibold text-destructive-foreground"
          >
            Sair
          </motion.button>
        </div>
      </BottomSheet>
    </>
  );
}
